import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { Box, HStack, IconButton, Text } from '@chakra-ui/react';
import { useAddress } from '@thirdweb-dev/react';
import React, { FC, useMemo } from 'react';
import { useToggle } from 'react-use';

import { CopyButton } from '@app/components/CopyButton';
import { EMPTY_ADDRESS } from '@app/constants/common';
import { formatWalletAddress, toOptionalFixed } from '@app/helpers/format';
import { usePairDetails } from '@app/hooks/pair/usePairDetails';
import { useSwapAvailablity } from '@app/hooks/swap/useSwapAvailablity';
import { useTokensRate, useTokensRateV2 } from '@app/hooks/swap/useTokensRate';
import { useTrade } from '@app/hooks/swap/useTrade';
import { InputsState } from '@app/screens/liquidity/AddLiquidity/hooks';
import { getSymbol } from '@app/helpers/symbol';

interface Props {
  state: InputsState;
  pairAddress: string | undefined;
}

export const LiquidityInfo: FC<Props> = ({ state, pairAddress }) => {
  const [isOpen, toggleOpen] = useToggle(false);
  const { fromRate, toRate } = useMemo(() => {
    if (
      !state.from.amount ||
      !state.to.amount ||
      !state.from.token ||
      !state.to.token
    ) {
      return {
        fromRate: 0,
        toRate: 0
      };
    }

    return {
      fromRate: +state.to.amount / +state.from.amount,
      toRate: +state.from.amount / +state.to.amount
    };
  }, [state.from.amount, state.from.token, state.to.amount, state.to.token]);

  const { data: pairDetails } = usePairDetails({
    id: pairAddress ? pairAddress.toLowerCase() : null
  });

  const address = useAddress();

  const { trades } = useTrade(state.from, state.to);

  const { isSwapAvailable } = useSwapAvailablity(
    state.from.token!,
    state.to.token!,
    trades
  );

  const { data: dataFrom } = useTokensRateV2({
    from: state.from.token!,
    to: state.to.token!,
    amount: '1',
    isSwapAvailable,
    trades,
    selectedTrade: undefined
  });
  const { swapOut: swapOutFrom } = dataFrom ?? {};

  const { data: dataTo } = useTokensRateV2({
    from: state.to.token!,
    to: state.from.token!,
    amount: '1',
    isSwapAvailable,
    trades,
    selectedTrade: undefined
  });
  const { swapOut: swapOutTo } = dataTo ?? {};

  const poolShare = useMemo(() => {
    if (!pairDetails || !address) {
      return 0;
    }

    const { liquidityPositions, totalSupply } = pairDetails;

    const position = liquidityPositions.find(
      lp => lp.user.id.toLowerCase() === address?.toLowerCase()
    );

    if (position) {
      const userDepositInPool = position.liquidityTokenBalance;

      return (+userDepositInPool * 100) / +totalSupply;
    }

    return 0;
  }, [address, pairDetails]);

  function renderInfoRow(
    label: string,
    value: string | number,
    copyValue?: boolean,
    rawValue?: string,
    highlightValue?: boolean
  ) {
    return (
      <HStack justifyContent="space-between" mb={2} color="whiteOpacity.70">
        <Text fontWeight={400} fontSize="14px" color="whiteOpacity.70">
          {label}
        </Text>
        {copyValue && rawValue ? (
          <HStack>
            <Text fontWeight={400} fontSize="14px" color="white">
              {value}
            </Text>
            <CopyButton text={rawValue} />
          </HStack>
        ) : (
          <Text
            color={highlightValue ? 'orange' : 'white'}
            fontWeight={400}
            fontSize="14px"
          >
            {value}
          </Text>
        )}
      </HStack>
    );
  }

  if (!state.from.token || !state.to.token) {
    return null;
  }

  function renderRates() {
    if (pairDetails) {
      const token0Symbol = getSymbol(pairDetails.token0.symbol);
      const token1Symbol = getSymbol(pairDetails.token1.symbol);

      return (
        <Box>
          <HStack>
            <Text color="neutral.300">
              1 {token0Symbol} = {toOptionalFixed(+pairDetails.token1Price, 5)}{' '}
              {token1Symbol}
            </Text>
          </HStack>

          <HStack>
            <Text color="neutral.300">
              1 {token1Symbol} = {toOptionalFixed(+pairDetails.token0Price, 5)}{' '}
              {token0Symbol}
            </Text>
          </HStack>
        </Box>
      );
    }

    return (
      <Box>
        <HStack>
          <Text color="neutral.300">
            1 {state.from.token?.symbol} = {toOptionalFixed(fromRate, 5)}{' '}
            {state.to.token?.symbol}
          </Text>
        </HStack>

        <HStack>
          <Text color="neutral.300">
            1 {state.to.token?.symbol} = {toOptionalFixed(toRate, 5)}{' '}
            {state.from.token?.symbol}
          </Text>
        </HStack>
      </Box>
    );
  }

  return (
    <>
      <Box
        // bg="transparent"
        width="100%"
        // p={2}
        mt="8px"
        p="16px"
        borderRadius="12px"
        bg="neutral.1000"
      >
        <HStack justifyContent="space-between">
          {renderRates()}
          <IconButton
            fontSize={16}
            onClick={toggleOpen}
            color="neutral.200"
            aria-label="Search database"
            icon={isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
          />
        </HStack>
        {isOpen && (
          <Box
            mt={4}
            borderTop="1px solid"
            borderColor="whiteOpacity.6"
            pt="12px"
          >
            {renderInfoRow(
              'Pair type',
              pairDetails?.isStable ? 'Stable' : 'Volatile',
              undefined,
              undefined,
              true
            )}
            {pairDetails &&
              swapOutFrom !== undefined &&
              renderInfoRow(
                `${state.from.token.symbol} swap rate`,
                `1 ${state.from.token.symbol} - ${swapOutFrom} ${state.to.token.symbol}`
              )}
            {pairDetails &&
              swapOutTo !== undefined &&
              renderInfoRow(
                `${state.to.token.symbol} swap rate`,
                `1 ${state.to.token.symbol} - ${swapOutTo} ${state.from.token.symbol}`
              )}
            {renderInfoRow(
              `${state.from.token.symbol} / ${state.to.token.symbol} liquidity ratio`,
              pairDetails ? +pairDetails.token1Price : fromRate
            )}
            {renderInfoRow(
              `${state.to.token.symbol} / ${state.from.token.symbol} liquidity ratio`,
              pairDetails ? +pairDetails.token0Price : toRate
            )}
            {renderInfoRow('Pool share', `${toOptionalFixed(poolShare, 5)}%`)}
            {renderInfoRow(
              'LP address',
              pairAddress && pairAddress !== EMPTY_ADDRESS
                ? formatWalletAddress(pairAddress)
                : 'n/a',
              Boolean(pairAddress && pairAddress !== EMPTY_ADDRESS),
              pairAddress
            )}
          </Box>
        )}
      </Box>
    </>
  );
};
